import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { Button, InputText, Modal, Tabs } from '@teo/components';
import { Portal } from '@/components/Portal';
import { clsx } from 'clsx';

export type ContentLinkType = 'lesson' | 'extra';

export type ContentLinkTargets = {
  lessons?: any[];
  extraInfo?: any[];
};

const targetName = (item: any) => item?.name || item?.assetName || '';

const escapeHtml = (str: string) =>
  String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const buildContentLinkHtml = (
  type: ContentLinkType,
  item: any,
  text: string
) => {
  const idAttr = item?.lessonId
    ? `data-lesson-id="${item.lessonId}"`
    : `data-asset-id="${item?.assetId}"`;
  return `<a href="#" class="teo-content-link" data-link-type="${type}" ${idAttr}>${escapeHtml(
    text || targetName(item)
  )}</a>`;
};

export const ContentLinkModal = ({
  targets,
  initialText = '',
  onClose,
  onInsert,
}: {
  targets?: ContentLinkTargets;
  initialText?: string;
  onClose: () => void;
  onInsert: (html: string) => void;
}) => {
  const { t } = useTranslation();
  const [type, setType] = useState<ContentLinkType>('lesson');
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<any>(null);
  const [text, setText] = useState(initialText);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const items = useMemo(() => {
    const list =
      (type === 'lesson' ? targets?.lessons : targets?.extraInfo) || [];
    const q = filter.trim().toLowerCase();
    return q
      ? list.filter((x) => targetName(x).toLowerCase().includes(q))
      : list;
  }, [type, targets, filter]);

  return (
    <Portal root="body">
      <div className="z-10 bg-white">
        <Modal isOpen={true}>
          <Modal.Header onClose={onClose}>
            <p className="font-semibold">{t('widgets.content_link.title')}</p>
          </Modal.Header>
          <Modal.Body>
            <Tabs
              activeName={type}
              onChange={(tab: any) => {
                setType(tab);
                setSelected(null);
              }}
            >
              <Tabs.TabButton name="lesson">
                <p className="text-base font-medium">
                  {t('widgets.content_link.type_lesson')}
                </p>
              </Tabs.TabButton>
              <Tabs.TabButton name="extra">
                <p className="text-base font-medium">
                  {t('widgets.content_link.type_extra')}
                </p>
              </Tabs.TabButton>
            </Tabs>

            <div className="mt-4">
              <InputText
                value={filter}
                onChange={(ev: any) => setFilter(ev.target.value)}
                placeholder={t('widgets.content_link.search')}
              />
            </div>

            <div className="mt-3 max-h-64 overflow-y-auto rounded-lg border border-grey-02">
              {items.length === 0 ? (
                <p className="p-3 text-sm text-grey-05">
                  {t('widgets.content_link.no_items')}
                </p>
              ) : (
                items.map((item: any, i: number) => [
                  item._group && item._group !== items[i - 1]?._group && (
                    <p
                      key={`group-${i}`}
                      className="bg-grey-01 px-3 py-1 text-xs font-medium text-grey-06"
                    >
                      {item._group}
                    </p>
                  ),
                  <button
                    key={item.usesId || item.id}
                    type="button"
                    className={clsx(
                      'block w-full px-3 py-2 text-left text-sm hover:bg-grey-01',
                      selected === item && 'bg-secondary-01 font-medium'
                    )}
                    onClick={() => setSelected(item)}
                  >
                    {targetName(item)}
                  </button>,
                ])
              )}
            </div>

            <div className="mt-4">
              <p className="mb-1 text-sm font-medium">
                {t('widgets.content_link.text')}
              </p>
              <InputText
                value={text}
                onChange={(ev: any) => setText(ev.target.value)}
                placeholder={targetName(selected)}
              />
            </div>
          </Modal.Body>
          <Modal.Footer
            cancelButtonLabel={t('widgets.upload_buttons.cancel_button')}
            onCancel={onClose}
          >
            <Button
              type="button"
              disabled={!selected}
              onClick={() =>
                onInsert(buildContentLinkHtml(type, selected, text.trim()))
              }
            >
              {t('widgets.upload_buttons.insert')}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Portal>
  );
};
